import { fetchAllEventsWithFinance, fetchPackages, fetchProducts, fetchSeasons } from "../lib/api.js";
import { formatTime, toLocalISODate } from "../lib/format.js";
import { escapeHtml, showError } from "../lib/ui.js";
import { eventDetailHtml } from "./eventDetail.js";

const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

/**
 * Calendario mensual: sirve sobre todo para ver de un vistazo si una fecha ya
 * está ocupada, algo que en una lista no se nota. Marca además los días que
 * caen dentro de una temporada de colonia.
 */
export async function renderMonthView(container) {
  container.innerHTML = `<h2>Calendario</h2><p>Cargando…</p>`;

  try {
    const [events, products, packages, seasons] = await Promise.all([
      fetchAllEventsWithFinance(),
      fetchProducts(),
      fetchPackages(),
      fetchSeasons(),
    ]);

    const today = new Date();
    let year = today.getFullYear();
    let month = today.getMonth() + 1; // 1-12

    const draw = () => {
      const pad = (n) => String(n).padStart(2, "0");
      const first = new Date(year, month - 1, 1);
      const daysInMonth = new Date(year, month, 0).getDate();
      // getDay(): 0 = domingo. Queremos que la semana arranque el lunes.
      const leading = (first.getDay() + 6) % 7;

      const byDate = new Map();
      for (const e of events) {
        if (!byDate.has(e.date)) byDate.set(e.date, []);
        byDate.get(e.date).push(e);
      }

      const cells = [];
      for (let i = 0; i < leading; i++) cells.push(`<div class="cal-cell empty"></div>`);

      for (let day = 1; day <= daysInMonth; day++) {
        const iso = `${year}-${pad(month)}-${pad(day)}`;
        const dayEvents = byDate.get(iso) ?? [];
        const inSeason = seasons.filter((s) => iso >= s.season_start && iso <= s.season_end);
        const isToday = iso === toLocalISODate(today);

        cells.push(`
          <div class="cal-cell${isToday ? " today" : ""}${dayEvents.length > 0 ? " busy" : ""}">
            <div class="cal-day">${day}</div>
            ${inSeason
              .map((s) => `<div class="cal-season" title="${escapeHtml(s.name)}">${escapeHtml(s.name)}</div>`)
              .join("")}
            ${dayEvents
              .map((e) => {
                const product = products.find((p) => p.id === e.product_id);
                const hora = e.start_time ? `${formatTime(e.start_time)} ` : "";
                return `<button class="cal-event" data-event="${escapeHtml(e.id)}"
                          style="background: ${escapeHtml(product?.color ?? "#4f6ef7")}"
                          title="${escapeHtml(hora + e.description)}">
                          ${hora ? `<strong>${hora}</strong>` : ""}${escapeHtml(e.description)}
                        </button>`;
              })
              .join("")}
          </div>
        `);
      }

      const monthName = first.toLocaleDateString("es-ES", { month: "long", year: "numeric" });

      container.innerHTML = `
        <div id="view">
          <h2>Calendario</h2>
          <div class="toolbar">
            <div class="row-actions">
              <button class="icon-btn" id="prev-month">← Anterior</button>
              <button class="icon-btn" id="today">Hoy</button>
              <button class="icon-btn" id="next-month">Siguiente →</button>
            </div>
            <strong style="text-transform: capitalize">${escapeHtml(monthName)}</strong>
          </div>

          <div class="panel">
            <div class="cal-grid">
              ${WEEKDAYS.map((d) => `<div class="cal-weekday">${d}</div>`).join("")}
              ${cells.join("")}
            </div>
          </div>

          <div class="panel" id="day-detail" hidden></div>
        </div>
      `;

      const view = container.querySelector("#view");

      view.querySelector("#prev-month").addEventListener("click", () => {
        if (month === 1) {
          month = 12;
          year -= 1;
        } else month -= 1;
        draw();
      });

      view.querySelector("#next-month").addEventListener("click", () => {
        if (month === 12) {
          month = 1;
          year += 1;
        } else month += 1;
        draw();
      });

      view.querySelector("#today").addEventListener("click", () => {
        year = today.getFullYear();
        month = today.getMonth() + 1;
        draw();
      });

      view.addEventListener("click", (e) => {
        const eventId = e.target.dataset?.event;
        if (!eventId) return;

        const event = events.find((x) => x.id === eventId);
        const detail = view.querySelector("#day-detail");
        detail.hidden = false;
        detail.innerHTML = eventDetailHtml(event, { products, packages });
        detail.scrollIntoView({ block: "nearest" });
      });
    };

    draw();
  } catch (error) {
    showError(container, error);
  }
}
